export default () => ({
    isOpen: false,
    trigger: null,
    content: null,
    template: null,
    animationFrame: 0,
    removeListeners: [],

    init() {
        this.trigger = this.$root.querySelector('.tooltip__icon');
        this.template = this.$root.querySelector('.tooltip__template');
        this.content = this.createContent();

        if (!this.trigger || !this.content) {
            return;
        }

        document.body.appendChild(this.content);

        this.trigger.setAttribute('aria-expanded', 'false');
        this.content.setAttribute('aria-hidden', 'true');
        this.content.classList.add('tooltip__content--body', 'tooltip__content--center');

        this.addListener(this.trigger, 'click', (event) => {
            event.preventDefault();

            if (this.$store.app.isMobile) {
                this.toggle();
            }
        });

        this.addListener(this.trigger, 'mouseenter', () => {
            if (!this.$store.app.isMobile) {
                this.open();
            }
        });

        this.addListener(this.trigger, 'mouseleave', () => {
            if (!this.$store.app.isMobile) {
                this.close();
            }
        });

        this.addListener(this.trigger, 'focus', () => {
            if (!this.$store.app.isMobile) {
                this.open();
            }
        });

        this.addListener(this.trigger, 'blur', () => {
            if (!this.$store.app.isMobile) {
                this.close();
            }
        });

        this.addListener(this.trigger, 'keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.toggle();
            }

            if (event.key === 'Escape') {
                this.close();
            }
        });

        this.addListener(document, 'pointerdown', (event) => {
            if (!this.isOpen || this.$root.contains(event.target) || this.content.contains(event.target)) {
                return;
            }

            this.close();
        });

        this.addListener(window, 'resize', () => this.schedulePositionUpdate(), { passive: true });
        this.addListener(window, 'scroll', () => this.schedulePositionUpdate(), { passive: true });
        this.addListener(window, 'tooltip-close', (event) => {
            if (event.detail?.source !== this.$root) {
                this.close();
            }
        });
    },

    destroy() {
        this.removeListeners.forEach((removeListener) => removeListener());
        this.removeListeners = [];

        window.cancelAnimationFrame(this.animationFrame);

        if (this.content?.parentElement) {
            this.content.remove();
        }
    },

    toggle() {
        if (this.isOpen) {
            this.close();
            return;
        }

        this.open();
    },

    open() {
        if (!this.content || !this.trigger) {
            return;
        }

        window.dispatchEvent(new CustomEvent('tooltip-close', {
            detail: {
                source: this.$root,
            },
        }));

        this.isOpen = true;
        this.$root.classList.add('tooltip--open');
        this.content.classList.add('tooltip__content--open');
        this.content.setAttribute('aria-hidden', 'false');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.updatePosition();
    },

    close() {
        if (!this.content || !this.trigger) {
            return;
        }

        this.isOpen = false;
        this.$root.classList.remove('tooltip--open');
        this.content.classList.remove('tooltip__content--open');
        this.content.setAttribute('aria-hidden', 'true');
        this.trigger.setAttribute('aria-expanded', 'false');
    },

    schedulePositionUpdate() {
        if (!this.isOpen || this.animationFrame) {
            return;
        }

        this.animationFrame = window.requestAnimationFrame(() => {
            this.animationFrame = 0;
            this.updatePosition();
        });
    },

    updatePosition() {
        if (!this.content || !this.trigger) {
            return;
        }

        const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
        const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
        const margin = 16;
        const gap = this.remToPx(1.5);
        const triggerRect = this.trigger.getBoundingClientRect();
        const triggerCenter = triggerRect.left + (triggerRect.width / 2);
        const contentRect = this.content.getBoundingClientRect();
        const contentWidth = contentRect.width;
        const contentHeight = contentRect.height;

        let align = 'center';
        let left = triggerCenter - (contentWidth / 2);
        let top = triggerRect.top - contentHeight - gap;
        let placement = 'top';

        if (left < margin) {
            left = margin;
            align = 'left';
        } else if ((left + contentWidth) > (viewportWidth - margin)) {
            left = viewportWidth - margin - contentWidth;
            align = 'right';
        }

        if (top < margin && (triggerRect.bottom + contentHeight + gap) <= (viewportHeight - margin)) {
            top = triggerRect.bottom + gap;
            placement = 'bottom';
        }

        const arrowLeft = this.clamp(triggerCenter - left, 12, contentWidth - 12);

        this.content.style.left = `${Math.round(left)}px`;
        this.content.style.top = `${Math.round(top)}px`;
        this.content.style.setProperty('--tooltip-arrow-left', `${Math.round(arrowLeft)}px`);
        this.setAlign(align);
        this.setPlacement(placement);
    },

    setAlign(align) {
        this.content.classList.remove('tooltip__content--left', 'tooltip__content--center', 'tooltip__content--right');
        this.content.classList.add(`tooltip__content--${align}`);
    },

    setPlacement(placement) {
        this.content.classList.toggle('tooltip__content--bottom', placement === 'bottom');
    },

    addListener(target, event, handler, options = false) {
        target.addEventListener(event, handler, options);
        this.removeListeners.push(() => target.removeEventListener(event, handler, options));
    },

    createContent() {
        const existingContent = this.$root.querySelector('.tooltip__content');

        if (existingContent) {
            return existingContent;
        }

        if (!this.template) {
            return null;
        }

        const content = document.createElement('div');
        content.className = 'tooltip__content';
        content.innerHTML = this.template.innerHTML.trim();

        return content;
    },

    remToPx(value) {
        return value * parseFloat(getComputedStyle(document.documentElement).fontSize);
    },

    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    },
});
